import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  Search,
  RefreshCw,
  UserPlus,
  Shield,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { Button, IconButton } from '../../components/ui/Button'
import { Badge, PlanBadge } from '../../components/ui/Badge'
import { Modal, ConfirmModal } from '../../components/ui/Modal'
import { SkeletonTable } from '../../components/ui/LoadingSpinner'
import { adminApi } from '../../lib/api'
import { formatDate, formatDateTime, timeAgo } from '../../lib/utils'
import { formatBillingPlanSummary } from '../../lib/subscription'
import toast from 'react-hot-toast'

const PAID_PLANS = ['starter', 'pro', 'business', 'enterprise']

function getBillingState(user) {
  const isPlatformAdmin = user.isPlatformAdmin === true
  const manualPlanOverride = Boolean(
    user.manualPlanOverride ||
    user.billingSource === 'manual_override' ||
    user.billing?.manualPlanOverride ||
    user.billing?.billingSource === 'manual_override'
  )
  const manualPlanOverridePlan =
    user.manualPlanOverridePlan ||
    (user.billingSource === 'manual_override' && (user.plan || user.subscriptionPlan)) ||
    user.billing?.manualPlanOverridePlan ||
    (user.billing?.billingSource === 'manual_override' && (user.billing?.plan || user.billing?.subscriptionPlan)) ||
    user.billing?.subscriptionPlan ||
    user.subscriptionPlan ||
    user.plan ||
    'free'

  if (manualPlanOverride) {
    return {
      plan: manualPlanOverridePlan,
      subscriptionStatus: '',
      currentPeriodEnd: '',
      cancelAtPeriodEnd: false,
      isPlatformAdmin,
      manualPlanOverride: true,
      manualPlanOverridePlan,
    }
  }
  return {
    plan: isPlatformAdmin
      ? 'admin'
      : user.billing?.subscriptionPlan ||
        user.billing?.plan ||
        user.subscriptionPlan ||
        user.plan ||
        'free',
    subscriptionStatus:
      isPlatformAdmin
        ? ''
        : user.billing?.subscriptionStatus ||
          user.subscriptionStatus ||
          '',
    currentPeriodEnd:
      isPlatformAdmin
        ? ''
        : user.billing?.currentPeriodEnd ||
          user.currentPeriodEnd ||
          '',
    cancelAtPeriodEnd: isPlatformAdmin
      ? false
      : Boolean(
          user.billing?.cancelAtPeriodEnd ||
          user.cancelAtPeriodEnd
        ),
    isPlatformAdmin,
    manualPlanOverride: false,
    manualPlanOverridePlan: '',
  }
}

function getAccountStatusLabel(user) {
  if (user.isPlatformAdmin === true) return 'Platform Admin'
  if (!user.enabled) return 'Disabled'
  if (user.status === 'CONFIRMED') return 'Active'
  return user.status || 'Pending'
}

function getBillingSummary(user) {
  const billing = getBillingState(user)
  if (billing.isPlatformAdmin) return 'Platform admin account'
  return formatBillingPlanSummary(billing.plan, {
    cancelAtPeriodEnd: billing.cancelAtPeriodEnd,
    currentPeriodEnd: billing.currentPeriodEnd,
    includePlanSuffix: false,
  })
}

function getBillingCategory(user) {
  const billing = getBillingState(user)
  if (billing.isPlatformAdmin) return 'platform_admin'
  if (billing.manualPlanOverride) return 'manual_override'
  if (billing.cancelAtPeriodEnd) return 'cancelling'
  if (billing.plan === 'free') return 'free'
  if (PAID_PLANS.includes(billing.plan)) return 'paid'
  return 'free'
}

function getBillingStatusLabel(user) {
  switch (getBillingCategory(user)) {
    case 'platform_admin':
      return 'Platform admin'
    case 'manual_override':
      return 'Manual override'
    case 'cancelling':
      return 'Cancelling'
    case 'paid':
      return 'Active paid'
    default:
      return 'Free'
  }
}

function getOperationalSummary(user) {
  return {
    jobCount: Number(user.jobCount ?? user.billing?.jobCount ?? 0),
    candidateCount: Number(
      user.candidateCount ?? user.cvCount ?? user.billing?.candidateCount ?? user.billing?.cvCount ?? 0
    ),
    lastActivityAt: user.lastActivityAt || user.billing?.lastActivityAt || '',
  }
}

function getLastActivityLabel(lastActivityAt) {
  if (!lastActivityAt) return 'No activity yet'
  const readable = formatDateTime(lastActivityAt)
  if (!readable) return 'No activity yet'
  const relative = timeAgo(lastActivityAt)
  return relative && relative !== readable ? `${readable} (${relative})` : readable
}

export function AdminPanel() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showCreate, setShowCreate] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [toggleConfirm, setToggleConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPlan, setNewPlan] = useState('free')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  async function loadUsers() {
    setLoading(true)
    try {
      const data = await adminApi.listUsers()
      setUsers(data.users || [])
    } catch (err) {
      toast.error('Failed to load users')
    }
    setLoading(false)
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (!newEmail.trim()) return
    setCreating(true)
    try {
      await adminApi.createUser(newEmail, newPlan)
      toast.success('User created successfully')
      setShowCreate(false)
      setNewEmail('')
      setNewPlan('free')
      loadUsers()
    } catch (err) {
      toast.error(err.message || 'Failed to create user')
    }
    setCreating(false)
  }

  async function handlePlanChange(username, plan) {
    try {
      await adminApi.updateUser(username, { plan })
      toast.success('Plan updated')
      loadUsers()
    } catch (err) {
      toast.error(err.message || 'Failed to update plan')
    }
  }

  async function handleRemoveOverride(username) {
    try {
      await adminApi.updateUser(username, { manualPlanOverride: false })
      toast.success('Manual override removed')
      loadUsers()
    } catch (err) {
      toast.error(err.message || 'Failed to remove override')
    }
  }

  async function handleToggleUser(username, nextEnabled) {
    setToggling(true)
    try {
      await adminApi.updateUser(username, { enabled: nextEnabled })
      toast.success(`User ${nextEnabled ? 'enabled' : 'disabled'}`)
      loadUsers()
    } catch (err) {
      toast.error(err.message || 'Failed to update user')
    }
    setToggling(false)
    setToggleConfirm(null)
  }

  async function handleDelete(username) {
    setDeleting(true)
    try {
      await adminApi.deleteUser(username)
      toast.success('User deleted')
      loadUsers()
    } catch (err) {
      toast.error(err.message || 'Failed to delete user')
    }
    setDeleting(false)
    setDeleteConfirm(null)
  }

  const filtered = users.filter((u) => {
    const matchesSearch =
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.username?.toLowerCase().includes(search.toLowerCase())

    if (!matchesSearch) return false
    const billing = getBillingState(u)
    if (statusFilter === 'active') return !!u.enabled
    if (statusFilter === 'free') return !billing.isPlatformAdmin && !billing.manualPlanOverride && billing.plan === 'free'
    if (statusFilter === 'paid') return !billing.isPlatformAdmin && !billing.manualPlanOverride && PAID_PLANS.includes(billing.plan)
    if (statusFilter === 'cancelling') return billing.cancelAtPeriodEnd
    if (statusFilter === 'manual_override') return billing.manualPlanOverride
    if (statusFilter === 'disabled') return !u.enabled
    if (statusFilter === 'platform_admin') return billing.isPlatformAdmin
    return true
  })

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-purple-600" />
            <h1 className="page-title">Admin Panel</h1>
          </div>
          <p className="page-subtitle">{users.length} total users</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={loadUsers}
            icon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreate(true)}
            icon={<UserPlus className="w-4 h-4" />}
          >
            Create user
          </Button>
        </div>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          className="input pl-9"
          placeholder="Search by email or username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4 mb-6">
        {[
          {
            label: 'Total Users',
            value: users.length,
            color: 'text-gray-900',
          },
          {
            label: 'Active',
            value: users.filter((u) => u.enabled).length,
            color: 'text-brand-600',
          },
          {
            label: 'Paid',
            value: users.filter((u) => {
              const billing = getBillingState(u)
              return !billing.isPlatformAdmin && !billing.manualPlanOverride && PAID_PLANS.includes(billing.plan)
            }).length,
            color: 'text-green-600',
          },
          {
            label: 'Free',
            value: users.filter((u) => {
              const billing = getBillingState(u)
              return !billing.isPlatformAdmin && !billing.manualPlanOverride && billing.plan === 'free'
            }).length,
            color: 'text-gray-600',
          },
          {
            label: 'Cancelling',
            value: users.filter((u) => getBillingState(u).cancelAtPeriodEnd).length,
            color: 'text-orange-600',
          },
          {
            label: 'Manual overrides',
            value: users.filter((u) => getBillingState(u).manualPlanOverride).length,
            color: 'text-blue-600',
          },
          {
            label: 'Disabled',
            value: users.filter((u) => !u.enabled).length,
            color: 'text-red-500',
          },
          {
            label: 'Platform admins',
            value: users.filter((u) => getBillingState(u).isPlatformAdmin).length,
            color: 'text-purple-600',
          },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <div className={`stat-value ${stat.color}`}>{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { id: 'all', label: 'All' },
          { id: 'active', label: 'Active' },
          { id: 'free', label: 'Free' },
          { id: 'paid', label: 'Paid' },
          { id: 'cancelling', label: 'Cancelling' },
          { id: 'manual_override', label: 'Manual override' },
          { id: 'disabled', label: 'Disabled' },
          { id: 'platform_admin', label: 'Platform admin' },
        ].map((filter) => (
          <button
            key={filter.id}
            type="button"
            onClick={() => setStatusFilter(filter.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === filter.id
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonTable rows={5} />
      ) : (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="col-span-4 table-header">User</div>
            <div className="col-span-3 table-header">Billing</div>
            <div className="col-span-2 table-header">Status</div>
            <div className="col-span-2 table-header">Joined</div>
            <div className="col-span-1 table-header">Actions</div>
          </div>

          <div className="divide-y divide-gray-50">
            {filtered.map((u, index) => {
              const billing = getBillingState(u)
              const billingPlan = billing.plan
              const billingSummary = getBillingSummary(u)
              const accountStatusLabel = getAccountStatusLabel(u)
              const isPlatformAdmin = billing.isPlatformAdmin
              const isManualOverride = billing.manualPlanOverride

              return (
                <motion.div
                  key={u.username}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.02 }}
                  className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors"
                >
                  <div className="col-span-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-brand-700">
                          {u.email?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {u.email}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {u.username?.substring(0, 20)}...
                        </p>
                        {!u.hasDynamoRecord && (
                          <p className="text-xs text-amber-600 mt-0.5">
                            Not yet provisioned in app data
                          </p>
                        )}
                        {(() => {
                          const activity = getOperationalSummary(u)
                          return (
                            <div className="mt-1 space-y-0.5">
                              <p className="text-xs text-gray-500">
                                {activity.jobCount} jobs / {activity.candidateCount} CVs / candidates
                              </p>
                              <p className="text-xs text-gray-500">
                                Last activity: {getLastActivityLabel(activity.lastActivityAt)}
                              </p>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-3">
                    {isPlatformAdmin ? (
                      <div className="space-y-1">
                        <PlanBadge plan="admin" />
                        <Badge variant="purple" size="sm">
                          Platform Admin
                        </Badge>
                        <p
                          className="text-xs text-gray-500"
                          title="Platform admin accounts cannot be deleted from this panel."
                        >
                          Platform admin accounts cannot be deleted from this panel.
                        </p>
                      </div>
                    ) : isManualOverride ? (
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <PlanBadge plan={billingPlan} />
                            <Badge variant="blue" size="sm">
                              Manual override
                            </Badge>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {billingSummary}
                          </p>
                          <p className="text-xs text-blue-700">
                            Set directly by an admin. Not backed by Stripe billing.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={billingPlan}
                            onChange={(e) => handlePlanChange(u.username, e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                          >
                            <option value="free">Free</option>
                            <option value="starter">Starter</option>
                            <option value="pro">Pro</option>
                            <option value="business">Business</option>
                            <option value="enterprise">Enterprise</option>
                          </select>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRemoveOverride(u.username)}
                          >
                            Remove override
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <PlanBadge plan={billingPlan} />
                            <Badge variant={billing.cancelAtPeriodEnd ? 'orange' : 'green'} size="sm">
                              {getBillingStatusLabel(u)}
                            </Badge>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {billingSummary}
                          </p>
                          {billing.cancelAtPeriodEnd && billing.currentPeriodEnd && (
                            <p className="text-xs font-medium text-amber-700">
                              Access remains active until {formatDate(billing.currentPeriodEnd)}
                            </p>
                          )}
                          {billing.subscriptionStatus && (
                            <p className="text-xs text-gray-500 capitalize">
                              Billing: {String(billing.subscriptionStatus).replace(/_/g, ' ')}
                            </p>
                          )}
                        </div>
                        <select
                          value={billingPlan}
                          onChange={(e) => handlePlanChange(u.username, e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                        >
                          <option value="free">Free</option>
                          <option value="starter">Starter</option>
                          <option value="pro">Pro</option>
                          <option value="business">Business</option>
                          <option value="enterprise">Enterprise</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="col-span-2">
                    <div className="space-y-1">
                      <span
                        className={`text-xs font-medium ${
                          u.enabled ? 'text-green-600' : 'text-red-500'
                        }`}
                      >
                        {accountStatusLabel}
                      </span>
                      {billing.manualPlanOverride ? (
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800">
                          Manual override
                        </span>
                      ) : billing.cancelAtPeriodEnd && (
                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                          Cancelling
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-2 text-sm text-gray-500">
                    {formatDateTime(u.createdAt)}
                  </div>

                  <div className="col-span-1 flex flex-col items-end gap-2">
                    {!isPlatformAdmin && (
                      <div className="flex items-center gap-1">
                        <IconButton
                          icon={
                            u.enabled ? (
                              <ToggleRight className="w-4 h-4 text-green-500" />
                            ) : (
                              <ToggleLeft className="w-4 h-4 text-gray-400" />
                            )
                          }
                          label={u.enabled ? 'Disable' : 'Enable'}
                          variant="ghost"
                          onClick={
                            u.enabled
                              ? () => setToggleConfirm({ username: u.username, email: u.email, nextEnabled: false })
                              : () => handleToggleUser(u.username, true)
                          }
                        />
                        <IconButton
                          icon={<Trash2 className="w-4 h-4" />}
                          label="Delete"
                          variant="danger"
                          onClick={() => setDeleteConfirm(u)}
                        />
                      </div>
                    )}
                    {isPlatformAdmin && (
                      <span
                        className="text-xs text-gray-400"
                        title="Platform admin accounts cannot be deleted from this panel."
                      >
                        Protected
                      </span>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      )}

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create new user"
        size="sm"
      >
        <div className="p-6">
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="form-group">
              <label className="label">Email address</label>
              <input
                type="email"
                className="input"
                placeholder="user@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Plan</label>
              <select
                className="input"
                value={newPlan}
                onChange={(e) => setNewPlan(e.target.value)}
              >
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="business">Business</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={creating}
              icon={<UserPlus className="w-4 h-4" />}
            >
              Create user
            </Button>
          </form>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm?.username)}
        title="Delete user?"
        message={`Are you sure you want to permanently delete ${deleteConfirm?.email}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        isOpen={!!toggleConfirm}
        onClose={() => setToggleConfirm(null)}
        onConfirm={() => handleToggleUser(toggleConfirm?.username, toggleConfirm?.nextEnabled)}
        title={toggleConfirm?.nextEnabled ? 'Enable user?' : 'Disable user?'}
        message={
          toggleConfirm?.nextEnabled
            ? `Are you sure you want to re-enable ${toggleConfirm?.email}?`
            : `Are you sure you want to disable ${toggleConfirm?.email}? This will remove access until re-enabled.`
        }
        confirmLabel={toggleConfirm?.nextEnabled ? 'Enable' : 'Disable'}
        variant={toggleConfirm?.nextEnabled ? 'primary' : 'danger'}
        loading={toggling}
      />
    </div>
  )
}
